import { Fragment } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Story } from "./CasesContent";
import CaseImage from "./CaseImage";
import CaseStats from "./CaseStats";
import CaseTechs from "./CaseTechs";
import { useInView } from "../../util/useInView";

// Case detalhado num painel só: cabeçalho com logos, faixa de resultados (o primeiro em rosa), o desafio ao lado
// do que fizemos e as tecnologias no rodapé. Os números contam e os blocos entram em cascata quando o painel aparece.
export default function CaseStory({ story }: { story: Story }) {
  const { t } = useTranslation();
  const titleId = `${story.id}-title`;
  const [ref, state] = useInView<HTMLElement>(0.15);
  const offset = story.results.length;

  return (
    <article ref={ref} className={`cz-case-panel${state}`} id={story.id} aria-labelledby={titleId}>
      <header className="cz-case-panel__head">
        <div className="cz-case-panel__lead">
          <span className="cz-case-panel__badge" aria-hidden="true">
            <story.Icon size={30} strokeWidth={1.6} />
          </span>
          <div className="cz-case-panel__heading">
            <span className="cz-eyebrow">{t("cases_page.labels.story_eyebrow", "Case em detalhe")}</span>
            <h2 className="cz-cases-title" id={titleId}>
              {story.title}
            </h2>
            <p className="cz-cases-subtitle">{story.subtitle}</p>
          </div>
        </div>
        {story.logos.length > 0 && (
          <div className="cz-case-panel__logos">
            {story.logos.map((logo, i) => (
              <Fragment key={logo.key}>
                {i > 0 && <span className="cz-case-panel__logo-sep" aria-hidden="true" />}
                <CaseImage
                  src={logo.src}
                  alt={logo.alt}
                  className={`cz-case-panel__logo cz-case-panel__logo--${logo.key}`}
                />
              </Fragment>
            ))}
          </div>
        )}
      </header>

      <CaseStats results={story.results.map((result, i) => ({ ...result, accent: i === 0 }))} state={state} />

      <div className="cz-case-panel__body">
        <div className="cz-case-panel__challenge" data-reveal style={{ "--i": offset } as React.CSSProperties}>
          <span className="cz-case-panel__context">
            {story.partners.map((logo, i) => (
              <Fragment key={logo.key}>
                {i > 0 && <span aria-hidden="true">+</span>}
                <img src={logo.src} alt={logo.alt} className={`cz-case-partner cz-case-partner--${logo.key}`} />
              </Fragment>
            ))}
            {story.partners.length > 0 && <span aria-hidden="true">·</span>}
            {story.context}
          </span>
          <span className="cz-case-label">{t("cases_page.labels.challenge")}</span>
          <p className="cz-case-panel__quote">{story.challenge}</p>
          {story.challengeNote && <p className="cz-case-panel__note">{story.challengeNote}</p>}
        </div>

        <div className="cz-case-panel__done">
          <span className="cz-case-label">{t("cases_page.labels.done")}</span>
          <ul className="cz-case-panel__checks">
            {story.done.map((item, i) => (
              <li key={item} data-reveal style={{ "--i": offset + 1 + i } as React.CSSProperties}>
                <span className="cz-case-panel__check" aria-hidden="true">
                  <Check size={14} strokeWidth={2.5} />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <footer className="cz-case-panel__foot">
        <CaseTechs techs={story.techs} />
        {story.link && (
          <a
            href={story.link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="cz-outline-btn cz-outline-btn--sm cz-outline-btn--pink cz-case-panel__link"
          >
            {story.link.label}
            <ArrowUpRight size={15} aria-hidden="true" />
          </a>
        )}
      </footer>
    </article>
  );
}
